import { useColumnVisibilityStorage } from "./useColumnVisibilityStorage";
import { useStaffPagination } from "./useStaffPagination";
import { useStaffSorting } from "./useStaffSorting";
import { useStaffFilters } from "./useStaffFilters";
import { useStaffData } from "./useStaffData";
import { useCompanyStaffSearchParams } from "@/features/categories/hooks/useCompanyStaffSearchParams";

export type { ColumnId, ColumnConfig, ColumnVisibility } from "../columnConfig";
export {
  AVAILABLE_COLUMNS as availableColumns,
  DEFAULT_COLUMN_VISIBILITY as defaultColumnVisibility,
} from "../columnConfig";

export function useCompanyStaffPanel() {
  const {
    pagination: urlPagination,
    sorting: urlSorting,
    filters: urlFilters,
    updateSearchParams,
  } = useCompanyStaffSearchParams();

  const {
    columnVisibility,
    columnVisibilityRecord: storedColumnVisibilityRecord,
    setColumnVisibility,
  } = useColumnVisibilityStorage();

  const pagination = useStaffPagination({
    initialPagination: urlPagination,
    onPaginationChange: (newPagination) => {
      updateSearchParams({ pagination: newPagination });
    },
  });

  const sorting = useStaffSorting({
    initialSorting: urlSorting,
    onSortingChange: (newSorting) => {
      const newPagination = { ...pagination.pagination, pageIndex: 0 };
      pagination.setPagination(newPagination);
      updateSearchParams({
        sorting: newSorting,
        pagination: newPagination,
      });
    },
  });

  const filters = useStaffFilters({
    initialFilters: urlFilters,
    urlFilters,
    onFiltersChange: (newFilters, currentPagination) => {
      updateSearchParams({
        filters: newFilters,
        pagination: currentPagination,
      });
    },
    onResetPagination: () => {
      pagination.setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    currentPagination: pagination.pagination,
  });

  const data = useStaffData({
    currentPage: pagination.currentPage,
    pageSize: pagination.pageSize,
    filters: filters.filters,
    sortField: sorting.sortField,
    sortDirection: sorting.sortDirection,
  });

  return {
    profiles: data.profiles,
    totalCount: data.totalCount,
    totalPages: data.totalPages,
    isLoading: data.isLoading,
    error: data.error,
    currentPage: pagination.currentPage,
    pageSize: pagination.pageSize,
    allowedPageSizes: pagination.allowedPageSizes,
    pagination: pagination.pagination,
    setPagination: pagination.setPagination,
    handlePageChange: pagination.handlePageChange,
    handlePageSizeChange: pagination.handlePageSizeChange,
    sortField: sorting.sortField,
    sortDirection: sorting.sortDirection,
    sorting: sorting.sorting,
    setSorting: sorting.setSorting,
    filters: filters.filters,
    setFilters: filters.setFilters,
    handleFilterChange: filters.handleFilterChange,
    clearFilters: filters.clearFilters,
    columnVisibility: storedColumnVisibilityRecord,
    columnVisibilityState: columnVisibility,
    setColumnVisibility,
  };
}
